import React, { useState } from 'react';
import { XIcon, SearchIcon, CheckIcon, ChevronRightIcon, ShieldIcon, ShoppingCartIcon, UsersIcon, FileTextIcon, FileSignatureIcon, CreditCardIcon, LayoutIcon, BarChart3Icon, AlertCircleIcon } from 'lucide-react';
interface ModulePermission {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
}
interface Module {
  id: string;
  name: string;
  icon: any;
  permissions: ModulePermission[];
}
interface LicensedModule {
  id: string;
  name: string;
  status: string;
}
interface PermissionsEditorProps {
  onClose: () => void;
  onSave: (modules: Module[]) => void;
  assignedModules?: string[];
  licensedModules: LicensedModule[];
}
export function PermissionsEditor({
  onClose,
  onSave,
  assignedModules = [],
  licensedModules = []
}: PermissionsEditorProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  // Define modules with their permissions
  const [modules, setModules] = useState<Module[]>([{
    id: 'accounts-payable',
    name: 'Accounts Payable',
    icon: FileTextIcon,
    permissions: [{
      id: 'view-invoices',
      name: 'View Invoices',
      description: 'View invoice details',
      enabled: true
    }, {
      id: 'create-invoices',
      name: 'Create Invoices',
      description: 'Enter new invoices',
      enabled: true
    }, {
      id: 'approve-invoices',
      name: 'Approve Invoices',
      description: 'Approve invoices for payment',
      enabled: false
    }, {
      id: 'match-invoices',
      name: 'Invoice Matching',
      description: 'Match invoices to POs and receipts',
      enabled: true
    }, {
      id: 'process-payments',
      name: 'Process Payments',
      description: 'Process invoice payments',
      enabled: false
    }]
  }, {
    id: 'purchase-order',
    name: 'Purchase Order',
    icon: ShoppingCartIcon,
    permissions: [{
      id: 'view-pos',
      name: 'View Purchase Orders',
      description: 'View all purchase orders',
      enabled: true
    }, {
      id: 'create-pos',
      name: 'Create Purchase Orders',
      description: 'Create new purchase orders',
      enabled: true
    }, {
      id: 'approve-pos',
      name: 'Approve Purchase Orders',
      description: 'Approve or reject purchase orders',
      enabled: false
    }, {
      id: 'manage-receipts',
      name: 'Manage Receipts',
      description: 'Create and manage goods receipts',
      enabled: true
    }]
  }, {
    id: 'contract-management',
    name: 'Contract Management',
    icon: FileSignatureIcon,
    permissions: [{
      id: 'view-contracts',
      name: 'View Contracts',
      description: 'View contract details',
      enabled: true
    }, {
      id: 'create-contracts',
      name: 'Create Contracts',
      description: 'Create new contracts',
      enabled: false
    }, {
      id: 'edit-contracts',
      name: 'Edit Contracts',
      description: 'Modify contract details',
      enabled: false
    }, {
      id: 'approve-contracts',
      name: 'Approve Contracts',
      description: 'Approve contract terms',
      enabled: false
    }, {
      id: 'manage-variations',
      name: 'Manage Variations',
      description: 'Create and approve contract variations',
      enabled: false
    }]
  }, {
    id: 'expense-management',
    name: 'Expense Management',
    icon: CreditCardIcon,
    permissions: [{
      id: 'view-expenses',
      name: 'View Expenses',
      description: 'View expense claims',
      enabled: true
    }, {
      id: 'create-expenses',
      name: 'Create Expenses',
      description: 'Submit expense claims',
      enabled: true
    }, {
      id: 'approve-expenses',
      name: 'Approve Expenses',
      description: 'Approve or reject expense claims',
      enabled: false
    }, {
      id: 'process-reimbursements',
      name: 'Process Reimbursements',
      description: 'Process expense reimbursements',
      enabled: false
    }]
  }, {
    id: 'vendor-onboarding',
    name: 'Vendor Onboarding',
    icon: UsersIcon,
    permissions: [{
      id: 'view-vendors',
      name: 'View Vendors',
      description: 'View vendor information',
      enabled: true
    }, {
      id: 'create-vendors',
      name: 'Create Vendors',
      description: 'Add new vendors to the system',
      enabled: true
    }, {
      id: 'edit-vendors',
      name: 'Edit Vendors',
      description: 'Modify vendor information',
      enabled: false
    }, {
      id: 'approve-vendors',
      name: 'Approve Vendors',
      description: 'Approve vendor onboarding',
      enabled: false
    }, {
      id: 'manage-compliance',
      name: 'Manage Compliance',
      description: 'Review vendor compliance documents',
      enabled: false
    }]
  }, {
    id: 'vendor-portal',
    name: 'Vendor Portal',
    icon: LayoutIcon,
    permissions: [{
      id: 'access-portal',
      name: 'Access Portal',
      description: 'Access the vendor portal',
      enabled: true
    }, {
      id: 'manage-portal-users',
      name: 'Manage Portal Users',
      description: 'Add and manage vendor portal users',
      enabled: false
    }, {
      id: 'configure-portal',
      name: 'Configure Portal',
      description: 'Configure portal settings and branding',
      enabled: false
    }]
  }]);
  // Filter modules to show ALL licensed modules that aren't expired or inactive
  const availableModules = modules.filter(module => {
    const licenseInfo = licensedModules.find(lm => lm.id === module.id);
    // Show modules that have a license and the status is NOT "Expired" or "Inactive"
    return licenseInfo && licenseInfo.status !== 'Expired' && licenseInfo.status !== 'Inactive';
  });
  // Get modules that are assigned but have inactive/expired licenses (to show warning)
  const inactiveAssignedModules = assignedModules.filter(moduleId => {
    const licenseInfo = licensedModules.find(lm => lm.id === moduleId);
    return !licenseInfo || licenseInfo.status === 'Expired' || licenseInfo.status === 'Inactive';
  });
  // Filter modules based on search
  const filteredModules = availableModules.filter(module => module.name.toLowerCase().includes(searchTerm.toLowerCase()) || module.permissions.some(perm => perm.name.toLowerCase().includes(searchTerm.toLowerCase())));
  const togglePermission = (moduleId: string, permissionId: string) => {
    setModules(prev => prev.map(module => {
      if (module.id === moduleId) {
        return {
          ...module,
          permissions: module.permissions.map(perm => perm.id === permissionId ? {
            ...perm,
            enabled: !perm.enabled
          } : perm)
        };
      }
      return module;
    }));
  };
  const getEnabledCount = (module: Module) => {
    const enabled = module.permissions.filter(p => p.enabled).length;
    const total = module.permissions.length;
    return `${enabled}/${total}`;
  };
  const handleSave = () => {
    onSave(modules);
    onClose();
  };
  return <div className="fixed inset-0 z-50 overflow-hidden" data-id="element-21008">
      <div className="absolute inset-0 overflow-hidden" data-id="element-21009">
        <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} data-id="element-21010" />
        <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex" data-id="element-21011">
          <div className="w-screen max-w-4xl" data-id="element-21012">
            <div className="h-full flex flex-col bg-white shadow-xl" data-id="element-21013">
              {/* Header */}
              <div className="px-6 py-6 bg-gradient-to-r from-indigo-600 to-indigo-700" data-id="element-21014">
                <div className="flex items-center justify-between" data-id="element-21015">
                  <div className="flex items-center space-x-3" data-id="element-21016">
                    <div className="flex-shrink-0" data-id="element-21017">
                      <div className="h-10 w-10 rounded-lg bg-white bg-opacity-20 flex items-center justify-center" data-id="element-21018">
                        <ShieldIcon className="h-6 w-6 text-white" data-id="element-21019" />
                      </div>
                    </div>
                    <div data-id="element-21020">
                      <h2 className="text-2xl font-bold text-white" data-id="element-21021">
                        Edit Permissions
                      </h2>
                      <p className="text-sm text-indigo-100 mt-1" data-id="element-21022">
                        Configure module access and permissions
                      </p>
                    </div>
                  </div>
                  <button onClick={onClose} className="rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white" data-id="element-21023">
                    <XIcon className="h-6 w-6" data-id="element-21024" />
                  </button>
                </div>
                {/* Search */}
                <div className="mt-4" data-id="element-21025">
                  <div className="relative" data-id="element-21026">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-21027">
                      <SearchIcon className="h-5 w-5 text-indigo-300" data-id="element-21028" />
                    </div>
                    <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-indigo-400 rounded-lg bg-white bg-opacity-20 text-white placeholder-indigo-200 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent sm:text-sm" placeholder="Search permissions..." data-id="element-21029" />
                  </div>
                </div>
              </div>
              {/* Content */}
              <div className="flex-1 overflow-y-auto" data-id="element-21030">
                {/* Warning for inactive modules */}
                {inactiveAssignedModules.length > 0 && !selectedModule && <div className="mx-6 mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-id="element-21031">
                    <div className="flex" data-id="element-21032">
                      <div className="flex-shrink-0" data-id="element-21033">
                        <AlertCircleIcon className="h-5 w-5 text-yellow-400" data-id="element-21034" />
                      </div>
                      <div className="ml-3" data-id="element-21035">
                        <h3 className="text-sm font-medium text-yellow-800" data-id="element-21036">
                          Inactive Module Licenses
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700" data-id="element-21037">
                          <p data-id="element-21038">
                            The following assigned modules have inactive or
                            expired licenses and their permissions cannot be
                            configured:
                          </p>
                          <ul className="list-disc list-inside mt-2 space-y-1" data-id="element-21039">
                            {inactiveAssignedModules.map(moduleId => {
                          const licenseInfo = licensedModules.find(lm => lm.id === moduleId);
                          return <li key={moduleId} data-id="element-21040">
                                  {licenseInfo?.name || moduleId} -{' '}
                                  {licenseInfo?.status || 'Unknown'}
                                </li>;
                        })}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>}
                {selectedModule ?
              // Detail view for selected module
              <div className="px-6 py-6" data-id="element-21041">
                    <button onClick={() => setSelectedModule(null)} className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6" data-id="element-21042">
                      <ChevronRightIcon className="h-4 w-4 mr-1 rotate-180" data-id="element-21043" />
                      Back to modules
                    </button>
                    {(() => {
                  const module = modules.find(m => m.id === selectedModule);
                  if (!module) return null;
                  const ModuleIcon = module.icon;
                  return <div data-id="element-21044">
                          <div className="flex items-center space-x-3 mb-6" data-id="element-21045">
                            <div className="h-12 w-12 rounded-lg bg-indigo-100 flex items-center justify-center" data-id="element-21046">
                              <ModuleIcon className="h-6 w-6 text-indigo-600" data-id="element-21047" />
                            </div>
                            <div data-id="element-21048">
                              <h3 className="text-xl font-semibold text-gray-900" data-id="element-21049">
                                {module.name}
                              </h3>
                              <p className="text-sm text-gray-500" data-id="element-21050">
                                {getEnabledCount(module)} permissions enabled
                              </p>
                            </div>
                          </div>
                          <div className="space-y-3" data-id="element-21051">
                            {module.permissions.map(permission => <div key={permission.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-colors" data-id="element-21052">
                                <div className="flex items-start justify-between" data-id="element-21053">
                                  <div className="flex items-start space-x-3 flex-1" data-id="element-21054">
                                    <button onClick={() => togglePermission(module.id, permission.id)} className={`flex-shrink-0 mt-0.5 h-5 w-5 rounded border-2 flex items-center justify-center transition-colors ${permission.enabled ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 hover:border-gray-400'}`} data-id="element-21055">
                                      {permission.enabled && <CheckIcon className="h-3 w-3 text-white" data-id="element-21056" />}
                                    </button>
                                    <div className="flex-1 min-w-0" data-id="element-21057">
                                      <p className="text-sm font-medium text-gray-900" data-id="element-21058">
                                        {permission.name}
                                      </p>
                                      <p className="text-xs text-gray-500 mt-1" data-id="element-21059">
                                        {permission.description}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>)}
                          </div>
                        </div>;
                })()}
                  </div> :
              // Module list view
              <div className="px-6 py-6" data-id="element-21060">
                    {filteredModules.length === 0 ? <div className="text-center py-12" data-id="element-21061">
                        <LayoutIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-21062" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-21063">
                          No active modules found
                        </h3>
                        <p className="mt-1 text-sm text-gray-500" data-id="element-21064">
                          {assignedModules.length === 0 ? 'No modules have been assigned to this user.' : availableModules.length === 0 ? 'All assigned modules have inactive or expired licenses.' : 'Try adjusting your search.'}
                        </p>
                      </div> : <div className="space-y-3" data-id="element-21065">
                        {filteredModules.map(module => {
                    const ModuleIcon = module.icon;
                    return <button key={module.id} onClick={() => setSelectedModule(module.id)} className="w-full bg-white border border-gray-200 rounded-lg p-4 hover:border-indigo-300 hover:shadow-sm transition-all text-left" data-id="element-21066">
                              <div className="flex items-center justify-between" data-id="element-21067">
                                <div className="flex items-center space-x-3 flex-1" data-id="element-21068">
                                  <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center" data-id="element-21069">
                                    <ModuleIcon className="h-5 w-5 text-gray-600" data-id="element-21070" />
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-21071">
                                    <p className="text-sm font-medium text-gray-900" data-id="element-21072">
                                      {module.name}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-21073">
                                      {getEnabledCount(module)} enabled
                                    </p>
                                  </div>
                                </div>
                                <ChevronRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0" data-id="element-21074" />
                              </div>
                            </button>;
                  })}
                      </div>}
                  </div>}
              </div>
              {/* Footer */}
              <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50" data-id="element-21075">
                <div className="flex justify-end space-x-3" data-id="element-21076">
                  <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-21077">
                    Cancel
                  </button>
                  <button onClick={handleSave} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-21078">
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}